import './update-isin.scss'

import { notification } from 'antd'
import axios from 'axios'
import { Formik, FormikHelpers } from 'formik'
import { DatePicker, Form, FormItem, Input, Select, SubmitButton } from 'formik-antd'
import React, { useEffect } from 'react'
import { Column } from 'react-display-flex'
import useSWR from 'swr'

import { fetchByDomain } from '../../../api'
import { filterProps, parseApiErrorResponse } from '../../../components/forms/utils'
import { config } from '../../../config'
import { currencies, getDefaultDomainByEnvironment } from '../../../models'
import { convertEntityValuesToFormValues } from '../../../util/convert-entity-values-to-form-values'
import { getEnvironment } from '../../../util/environment'
import { Issuer } from '../../Issuers/models'
import { messages } from '../messages'
import {
  Isin,
  isinFormInitialValues,
  IsinFormValues,
  isinInstrumentTypes,
  transformFormValuesToIsinPayload,
} from '../models'
import { isinValidationSchema } from '../validation'

interface UpdateIsinFormProps {
  isin: Isin
  onSuccess: () => void
}

export const UpdateIsin = ({ isin, onSuccess }: UpdateIsinFormProps) => {
  const environment = getEnvironment()
  const [updateFormInitialValues, setUpdateFormInitialValues] = React.useState<IsinFormValues>(isinFormInitialValues)

  const { data: issuers } = useSWR<Issuer[]>(['/issuers', getDefaultDomainByEnvironment(environment)], fetchByDomain)

  useEffect(() => {
    isin?.isin && setUpdateFormInitialValues(convertEntityValuesToFormValues(isinFormInitialValues, isin))
  }, [isin])

  const onSubmit = async (
    isinFormValues: IsinFormValues,
    { setSubmitting, resetForm, setErrors }: FormikHelpers<IsinFormValues>,
  ) => {
    try {
      const updateIsinPayload = transformFormValuesToIsinPayload(isinFormValues)
      delete updateIsinPayload.isin

      await axios.put(`/isins/${isin.id}`, updateIsinPayload)

      resetForm()
      notification.success({ message: messages.common.success, description: messages.update.success })
      onSuccess()
    } catch (error) {
      const formErrors = parseApiErrorResponse(error)
      setErrors(formErrors)
      setSubmitting(false)
      notification.error({ message: messages.common.error, description: messages.update.error })
    }
  }

  return (
    <Formik
      initialValues={updateFormInitialValues}
      validationSchema={isinValidationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ values }) => (
        <Form name="update-isin">
          <FormItem name="isin" label="ISIN" required>
            <Input name="isin" arial-label="isin" placeholder="ISIN" disabled />
          </FormItem>
          <FormItem name="issuer" label="Issuer Code" required>
            <Select
              name="issuer"
              aria-label="issuer"
              placeholder="Issuer Code"
              loading={!issuers}
              showSearch
              options={issuers?.map(({ code, shortName }) => ({
                label: `${code} - ${shortName}`,
                value: code,
              }))}
              {...filterProps}
              disabled
            />
          </FormItem>
          <FormItem name="type" label="Instrument Type" required>
            <Select
              name="type"
              aria-label="type"
              placeholder="Instrument Type"
              showSearch
              options={Object.values(isinInstrumentTypes).map((instrumentValue: string) => ({
                label: instrumentValue,
                value: instrumentValue,
              }))}
              {...filterProps}
              disabled
            />
          </FormItem>
          <FormItem name="maturity" label="Maturity Date" required>
            <DatePicker
              name="maturity"
              aria-label="maturity"
              placeholder="Maturity Date"
              format={config.defaultDateFormat}
            />
          </FormItem>
          <FormItem name="currency" label="Currency">
            <Select
              name="currency"
              aria-label="currency"
              placeholder="Select a currency"
              showSearch
              allowClear
              options={currencies.map((currency: string) => ({
                label: currency,
                value: currency,
              }))}
              {...filterProps}
            />
          </FormItem>
          {values.type === isinInstrumentTypes.bond && (
            <FormItem name="coupon" label="Coupon" required>
              <Input name="coupon" aria-label="coupon" placeholder="Coupon" type="number" />
            </FormItem>
          )}
          {[isinInstrumentTypes.frn, isinInstrumentTypes.frtd].includes(values.type) && (
            <>
              <FormItem name="frnIssueMargin" label="FRN Issue Margin">
                <Input
                  name="frnIssueMargin"
                  aria-label="frnIssueMargin"
                  placeholder="FRN Issue Margin"
                  type="number"
                  allowClear
                />
              </FormItem>
              <FormItem name="frnBenchmark" label="FRN Benchmark">
                <Input name="frnBenchmark" aria-label="frnBenchmark" placeholder="FRN Benchmark" allowClear />
              </FormItem>
            </>
          )}
          <Column>
            <SubmitButton htmlType="submit" type="primary">
              Update ISIN
            </SubmitButton>
          </Column>
        </Form>
      )}
    </Formik>
  )
}
